import React, {useMemo, useState} from 'react';
import {Box, Grid, IconButton, Snackbar, Tooltip, Typography, Button, useTheme} from "@mui/material";
import {QuerySummaryTable} from "../QuerySummaryTable/QuerySummaryTable";
import {useQuerySdqlData, useQuerySdqlSummaryData} from "../../../../../hooks/getNbaMatchups/querySdqlData";
import {NbaGame} from "../../../../../types/cbbTypes";
import {useGetSearchTrends} from "../../../../../hooks/getSearchTrends/getSearchTrends";
import {QueryStringButtonGroup} from "../../../../molecules/ButtonRow/QueryStringButtonGroup";
import {TrendTypeSelector} from "./components/TrendTypeSelector/TrendTypeSelector";
import {SaveTrendDialog} from "../../../../organisms/SaveTrendDialog/SaveTrendDialog";
import {SearchTrend} from "../../../../../types/nbaTypes";
import {getActiveGames, getImageUrl} from "../../../../../utils/helperFunctions";
import {useAuth} from "../../../../../AuthContext/authContext";
import type {Sport} from "../../../../../types/types";
import {QueryResultsTable} from "../../../../organisms/BetboardTable/components/QueryResultsTable";
import {localStateButtonStyle} from "../../../../../utils/constants";
import {Info} from "@mui/icons-material";
import {LoadingTable} from "../../../../atoms/LoadingSkeleton/LoadingTable";
import {LoadingSkeleton} from "../../../../atoms/LoadingSkeleton/LoadingSkeleton";
import {LoadingSummaryTable} from "../../../../atoms/LoadingSkeleton/LoadingSummaryTable";
import {SaveTrendButton} from "../../../../atoms/SaveTrendButton/SaveTrendButton";
import useIsMobile from "../../../../../hooks/utilHooks/useIsMobile/useIsMobile";

interface TrendFinderProps {
    currentMatchup?: NbaGame;
    queryString: string;
    sport: Sport;
    handleChange: (sdqlString: string, reset: boolean) => void;
    teamTrends: SearchTrend[]
    oTeamTrends: SearchTrend[]
    seasonTrends: SearchTrend[]
    setSwitchTeamAndOTeam?: (switchTeamAndOTeam: boolean) => void;
    switchTeamAndOTeam?: boolean;
}

export const TrendFinder = (
    {
        currentMatchup,
        queryString,
        handleChange,
        sport,
        setSwitchTeamAndOTeam,
        switchTeamAndOTeam,
        teamTrends = [],
        oTeamTrends = [],
        seasonTrends = []
    }: TrendFinderProps
) => {
    const [isSaveTrendDialogOpen, setIsSaveTrendDialogOpen] = useState(false);
    const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const {
        data: sdqlData,
        error: SdqlError,
        loading: SdqlGameDataLoading
    } = useQuerySdqlData(queryString, sport) || {}

    const {
        data: sdqlSummaryData,
        error: sdqlSummaryError,
        loading: sdqlSummaryDataLoading
    } = useQuerySdqlSummaryData(queryString, sport) || {}

    const {
        team: currentMatchupTeam,
        oTeam: currentMatchupOTeam,
        site,
        line,
        total,
        streak,
        atsStreak,
        ouStreak,
        oStreak,
        oAtsStreak,
        oOuStreak,
        wins,
        losses,
        rest,
        oWins,
        oRest,
        oLosses
    } = currentMatchup || {};

    const {data: searchTrendData, loading: searchTrendsLoading} = useGetSearchTrends({
        sport,
        queryString,
        rest: +(rest || 0),
        team: currentMatchupTeam || '',
        oTeam: currentMatchupOTeam || '',
        line: +(line || 0),
        total: +(total || 0),
        oRest: +(oRest || 0),
        streak: +(streak || 0),
        oStreak: +(oStreak || 0),
        atsStreak: +(atsStreak || 0),
        oAtsStreak: +(oAtsStreak || 0),
        ouStreak: +(ouStreak || 0),
        oOuStreak: +(oOuStreak || 0),
        winPercentage: +(wins || 0) / (+(wins || 0) + +(losses || 0)),
        oWinPercentage:  +(oWins || 0) / (+(oWins || 0) + +(oLosses || 0)),
    })

    const {loggedInUserEmail} = useAuth()
    const theme = useTheme()
    const isTokenValid = !!loggedInUserEmail

    const defaultTrendTypes = ['team', 'o:team', 'season', 'site']
    const allTrends = [
        ...(teamTrends || []),
        ...(oTeamTrends || []),
        ...(seasonTrends || []),
    ]
    const {allTrendTypes, playoffTrendTypes} = allTrends?.reduce((uniqueTrendTypes: { allTrendTypes: string[], playoffTrendTypes: string[] }, el: { trendParameter: string, isPlayoffTrend: boolean }) => {
        if (!uniqueTrendTypes.allTrendTypes.includes(el.trendParameter) && !defaultTrendTypes.includes(el.trendParameter) && !el.isPlayoffTrend) {
            uniqueTrendTypes.allTrendTypes.push(el.trendParameter)
        }
        if (!uniqueTrendTypes.playoffTrendTypes.includes(el.trendParameter) && !defaultTrendTypes.includes(el.trendParameter) && el.isPlayoffTrend) {
            uniqueTrendTypes.playoffTrendTypes.push(el.trendParameter)
        }
        return uniqueTrendTypes;
    }, {allTrendTypes: [], playoffTrendTypes: []})
    const [selectedTrendParameter, setSelectedTrendParameter] = useState(allTrendTypes?.[0] || 'line');

    const { teamSiteTrends } = teamTrends.reduce(
        (acc: {teamSiteTrends: SearchTrend[]}, trend: SearchTrend) => {
            if (trend.trendParameter === 'site' && acc) acc.teamSiteTrends.push(trend)
            return acc;
        }, {teamSiteTrends: []})
    const oTeamSiteTrends = oTeamTrends?.filter((trend: { trendParameter: string }) =>
        trend.trendParameter === 'site')
    const selectedTeamNameTrends = teamTrends?.filter((trend: { trendParameter: string }) =>
        trend.trendParameter === 'team')
    const selectedOTeamNameTrends = oTeamTrends?.filter((trend: { trendParameter: string }) =>
        trend.trendParameter === 'o:team')
    const selectedTeamSearchTrends = teamTrends?.filter((trend: { trendParameter: string }) =>
        trend.trendParameter === selectedTrendParameter)
    const selectedOTeamSearchTrends = oTeamTrends?.filter((trend: { trendParameter: string }) =>
        trend.trendParameter === selectedTrendParameter)
    const activeGames = useMemo(() => getActiveGames(sdqlData), [sdqlData]);
    const isMobile = useIsMobile()
    let teamNameToUse = currentMatchup?.team
    let oTeamNameToUse = currentMatchup?.oTeam
    if(!!currentMatchup && 'school' in currentMatchup && 'oSchool' in currentMatchup) {
        teamNameToUse = (currentMatchup.school || '') as string;
        oTeamNameToUse = (currentMatchup.oSchool || '') as string;
    }

    return (
        <Box style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: '1184px'}}>
            <Grid
                display="flex"
                justifyContent="space-between"
                item
                container
                xs={12}
                mt={1}
            >
                <Grid item container xs={2}><p></p></Grid>
                <Grid
                    item
                    container
                    xs={2}
                    display="flex"
                    flexDirection={'column'}
                    justifyContent="center"
                    alignItems="center"
                    style={{borderRadius: 5, padding: 0, margin: 0, background: 'white'}}
                >
                    <Box>

                        {getImageUrl((switchTeamAndOTeam ? oTeamNameToUse : teamNameToUse) || '', sport, 30, 44)}
                    </Box>
                    <Typography
                        component="p"
                        variant="caption"
                        color={theme.palette.grey[500]}
                        align="center"

                    >
                        Team Conditions
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={4}
                    mt={2}
                    container
                    display="flex"
                    justifyContent="center"
                    alignContent="center"
                    flexDirection={isMobile ? 'column' : 'row'}
                >
                    {currentMatchup?.team ?
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={
                                    () => {
                                        setSwitchTeamAndOTeam && setSwitchTeamAndOTeam(!switchTeamAndOTeam);
                                        handleChange('', true);
                                    }
                                }
                                sx={{
                                    ...localStateButtonStyle,
                                    '&:hover': !!setSwitchTeamAndOTeam ? {
                                        backgroundColor: 'lightgrey',
                                        cursor: 'pointer',
                                    } : '',
                                    textTransform: 'none',
                                }}
                            >
                                {`Switch Teams`}
                                <Tooltip
                                    title={`Switching team and opponent will reset the query string,
                                and you will be viewing trends from the opponent's point of view.`}
                                >
                                    <IconButton size='small'>
                                        <Info
                                            style={{
                                                fontSize: '11pt',
                                                paddingBottom: !isMobile ? '2px' : '',
                                                color: 'grey'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleChange(``, true)}
                                sx={{
                                    ...localStateButtonStyle,
                                    '&:hover': !!setSwitchTeamAndOTeam ? {
                                        backgroundColor: 'lightgrey',
                                        cursor: 'pointer',
                                    } : '',
                                    textTransform: 'none',
                                    marginLeft: !isMobile ? '8px' : '',
                                }}
                            >
                                Reset
                            </Button>
                        </>
                        : <p></p>
                    }
                </Grid>
                <Grid
                    item
                    container
                    xs={2}
                    display="flex"
                    flexDirection={'column'}
                    justifyContent="center"
                    alignItems="center"
                    style={{borderRadius: 5, padding: 0, margin: 0, background: 'white'}}
                >
                    <Box>
                        {getImageUrl((switchTeamAndOTeam ? teamNameToUse : oTeamNameToUse) || '', sport, 30, 44)}
                    </Box>
                    <Typography
                        component="p"
                        variant="caption"
                        color={theme.palette.grey[500]}
                        align="center"

                    >
                        Opponent Conditions
                    </Typography>
                </Grid>
                <Grid item container xs={2}><p></p></Grid>
                {
                    selectedTeamNameTrends?.length > 0
                    && selectedOTeamNameTrends?.length > 0
                    && (
                        <>
                        <Grid item container xs={2}><p></p></Grid>
                    <Grid
                        item
                        xs={2}
                        mt={1}
                    >
                        <QueryStringButtonGroup
                            queryString={queryString}
                            handleChange={handleChange}
                            buttonConfigs={selectedTeamNameTrends}
                            loading={searchTrendsLoading}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={4}
                        container
                        display="flex"
                        justifyContent="center"
                        alignContent="center"
                        mt={1}
                    >
                    <Typography
                        component="p"
                        align="center"
                        variant="caption"
                        color={theme.palette.grey[500]}
                    >
                        team
                    </Typography>
                        </Grid>
                        <Grid item xs={2} mt={1}>
                        <QueryStringButtonGroup
                            queryString={queryString}
                            handleChange={handleChange}
                            buttonConfigs={selectedOTeamNameTrends}
                            loading={searchTrendsLoading}
                        />
                        </Grid>
                        <Grid item container xs={2}><p></p></Grid>
                    </>
                    )
                }
                <Grid item container xs={2} mb={currentMatchupTeam ? 0 : 1}><p></p></Grid>
                <Grid item xs={2} mt={1} mb={currentMatchupTeam ? 0 : 1}>
                    <QueryStringButtonGroup
                        queryString={queryString}
                        handleChange={handleChange}
                        buttonConfigs={teamSiteTrends}
                        loading={searchTrendsLoading}
                    />
                </Grid>
                <Grid
                    item
                    xs={4}
                    container
                    display="flex"
                    justifyContent="center"
                    alignContent="center"
                    mt={1}
                    mb={currentMatchupTeam ? 0 : 1}
                >
                    <Typography
                        component="p"
                        align="center"
                        variant="caption"
                        color={theme.palette.grey[500]}
                    >
                        site
                    </Typography>
                </Grid>
                <Grid item xs={2} mt={1} mb={currentMatchupTeam ? 0 : 1}>
                    <QueryStringButtonGroup 
                        queryString={queryString} 
                        handleChange={handleChange}
                        buttonConfigs={oTeamSiteTrends}
                        loading={searchTrendsLoading}
                    />
                </Grid>
                <Grid item container xs={2}><p></p></Grid>
                {!isMobile && <Grid item container xs={1}><p></p></Grid>}
                <QueryStringButtonGroup
                    xs={4}
                    mt={1}
                    queryString={queryString}
                    handleChange={handleChange}
                    buttonConfigs={selectedTeamSearchTrends}
                    loading={searchTrendsLoading}
                />
                <Grid
                    item
                    xs={isMobile ? 4 : 2}
                    container
                    display="flex"
                    justifyContent="center"
                    alignContent="center"
                >
                    <Typography
                        component="p"
                        align="center"
                        variant="caption"
                        color={theme.palette.grey[500]}
                    >
                        selected parameter:{` `}
                        <Typography
                            component="span"
                            variant="caption"
                            style={{fontWeight: '1000'}}
                        >
                            {selectedTrendParameter}
                        </Typography>
                    </Typography>
                </Grid>
                <QueryStringButtonGroup
                    justifyContent="center"
                    xs={4}
                    mt={1}
                    queryString={queryString}
                    handleChange={handleChange}
                    buttonConfigs={selectedOTeamSearchTrends}
                    loading={searchTrendsLoading}
                />
                {!isMobile && <Grid item container xs={1}><p></p></Grid>}
                <TrendTypeSelector
                    mt={2}
                    trendType={selectedTrendParameter}
                    setTrendType={setSelectedTrendParameter}
                    trendTypes={allTrendTypes}
                    playoffTrendTypes={playoffTrendTypes}
                />
            </Grid>
            <QueryStringButtonGroup
                queryString={queryString}
                handleChange={handleChange}
                buttonConfigs={seasonTrends}
                loading={searchTrendsLoading}
                flexDirection={'row'}
                label={'Data From:'}
                xs={12}
                mb={2}
            />
            <Grid
                item
                container
                xs={12}
                pl={3.5}
                pr={3.5}
                mb={2}
                display="flex"
                justifyContent="center"
                alignItems="center"

            >
                <SaveTrendButton
                    handleSubmit={() => setIsSaveTrendDialogOpen(true)}
                    queryString={queryString}
                />
                <Typography
                    align="center"
                    variant="caption"
                    component="p"
                    color="grey"
                    pr={3.5}
                    pl={2}
                    pt={1}
                >
                    Records & Averages for {sport.toUpperCase()} Query String:
                    <Typography
                        variant="caption"
                        style={{fontWeight: 1000}}
                    >
                        {` ${queryString}`}
                    </Typography>
                </Typography>
            </Grid>
            {sdqlSummaryDataLoading && <LoadingSummaryTable/>}
            {sdqlSummaryData?.sdqlSummaryQuery?.map((dataItem: any, ind:number) => {
                return (
                    <QuerySummaryTable
                        summaryData={dataItem ?? []}
                        setIsSaveTrendDialogOpen={setIsSaveTrendDialogOpen}
                        loading={sdqlSummaryDataLoading}
                        queryString={queryString}
                        sport={sport}
                        key={ind}
                    />
                )}
            )}
            <Grid item xs={4}><p></p></Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowResults(!showResults)}
                    disabled={sdqlData?.sdqlQuery?.length > 150}
                    sx={{
                        ...localStateButtonStyle,
                        '&:hover': !!setSwitchTeamAndOTeam ? {
                            backgroundColor: 'lightgrey',
                            cursor: 'pointer',
                        } : '',
                        textTransform: 'none',
                    }}
                >
                    {showResults ? 'Hide' : 'View'} Game Results
                </Button>
            </Grid>
            <Grid item xs={4}><p></p></Grid>
            {showResults && sdqlData?.sdqlQuery?.length < 151 && (
                <QueryResultsTable
                    rows={sdqlData?.sdqlQuery}
                    limit={3}
                    sport={sport}
                />
            )}
            <SaveTrendDialog
                isOpen={isSaveTrendDialogOpen}
                onClose={() => setIsSaveTrendDialogOpen(false)}
                queryString={queryString}
                isSnackBarOpen={isSnackBarOpen}
                isTokenValid={isTokenValid}
                setIsSnackBarOpen={setIsSnackBarOpen}
                trend={queryString}
                sport={sport}
                activeGames={activeGames}
                sdqlSummaryData={sdqlSummaryData}
            />
            <Snackbar
                open={isSnackBarOpen}
                autoHideDuration={5000}
                onClose={() => setIsSnackBarOpen(false)}
                message="Saved trend successfully."
            />
        </Box>
    )

}